import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { fetchAdminSecurityMe, probePrivilegedChrome } from '@/lib/privilegedChrome';

const PrivilegedChromeCtx = createContext(null);

const IDLE = { ready: false, allowed: false, status: null, terminal: false };

function applyProbeResult(prev, result) {
  if (result.authExpired) {
    return { ready: true, allowed: false, status: null, terminal: false };
  }
  if (result.terminal) {
    return {
      ready: true,
      allowed: result.allowed,
      status: result.status,
      terminal: true,
    };
  }
  // Transient failure (429/5xx/network): keep a prior successful chrome decision.
  if (prev.terminal && prev.allowed) {
    return { ready: true, allowed: true, status: prev.status, terminal: true };
  }
  return { ready: true, allowed: false, status: null, terminal: false };
}

export function PrivilegedChromeProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [state, setState] = useState(IDLE);
  const inFlight = useRef(null);
  const generation = useRef(0);
  const prevUserId = useRef(null);

  const runProbe = useCallback(async () => {
    if (!userId) {
      setState({ ready: true, allowed: false, status: null, terminal: false });
      return;
    }
    if (inFlight.current) return inFlight.current;

    const gen = generation.current;
    const task = probePrivilegedChrome(() => fetchAdminSecurityMe(api))
      .then((result) => {
        if (generation.current !== gen) return result;
        setState((prev) => applyProbeResult(prev, result));
        return result;
      })
      .finally(() => {
        if (inFlight.current === task) inFlight.current = null;
      });
    inFlight.current = task;
    return task;
  }, [userId]);

  useEffect(() => {
    generation.current += 1;
    inFlight.current = null;
    if (!userId) {
      prevUserId.current = null;
      setState({ ready: true, allowed: false, status: null, terminal: false });
      return undefined;
    }
    const userChanged = prevUserId.current !== userId;
    prevUserId.current = userId;
    if (userChanged) {
      setState(IDLE);
    } else {
      // Same user, effect re-fired — keep prior chrome visible while re-probing.
      setState((prev) => ({ ...prev, ready: false }));
    }
    runProbe();
    return undefined;
  }, [userId, runProbe]);

  const refresh = useCallback(async () => {
    generation.current += 1;
    inFlight.current = null;
    if (!userId) {
      setState({ ready: true, allowed: false, status: null, terminal: false });
      return;
    }
    setState((prev) => ({ ...prev, ready: false }));
    await runProbe();
  }, [userId, runProbe]);

  const applyStatus = useCallback((data) => {
    if (!data) return;
    setState({
      ready: true,
      allowed: true,
      status: data,
      terminal: true,
    });
  }, []);

  const value = useMemo(
    () => ({
      ready: state.ready,
      allowed: state.allowed,
      status: state.status,
      terminal: state.terminal,
      refresh,
      applyStatus,
    }),
    [state, refresh, applyStatus],
  );

  return (
    <PrivilegedChromeCtx.Provider value={value}>
      {children}
    </PrivilegedChromeCtx.Provider>
  );
}

export function usePrivilegedChrome() {
  const ctx = useContext(PrivilegedChromeCtx);
  if (!ctx) throw new Error('usePrivilegedChrome must be used within PrivilegedChromeProvider');
  return ctx;
}

export default PrivilegedChromeProvider;
